import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { addMonths, differenceInCalendarMonths } from 'date-fns';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { AddContractRequest } from '../../../models/maintenance-costing-request.model';
import { ContractType } from '../../../enums/maintenance-costing.enum';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { parseLocaleNumber } from '../../../../../../_shared/helpers/number-input.helper';
import { AccountAssetScopeSelectorComponent, ScopeSelection } from '../../../../../../_shared/components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ContractTypeOption } from '../../../models/maintenance-costing.model';
import { formatPickerDate, parsePickerDate, toApiDate } from '../../../helpers/picker-date.helper';

@Component({
  selector: 'app-add-contract-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FlatpickrDatePickerComponent, NgbDropdownModule, AccountAssetScopeSelectorComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-contract-modal.component.html',
  styleUrl: './add-contract-modal.component.scss'
})
export class AddContractModalComponent implements OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private maintenanceCostingService: MaintenanceCostingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private authService: AuthService = inject(AuthService);

  private readonly numericControls: string[] = ['variableCpk', 'contractMaxKm', 'fixedMonthlyCost', 'contractualKmMonth'];
  private readonly minContractMonths: number = 1;

  contractForm: FormGroup;
  currencySymbol: string = '';
  distanceSymbol: string = '';
  currentStep: number = 1;
  isSaving: boolean = false;

  selectedAssets: ScopeSelection[] = [];
  selectedContractType: ContractTypeOption | null = null;

  readonly contractTypes: ContractTypeOption[] = [
    { labelKey: 'contractTypes.fmc', value: ContractType.FullMaintenanceContract },
    { labelKey: 'contractTypes.fml', value: ContractType.FullMaintenanceLease },
    { labelKey: 'contractTypes.serviceOnly', value: ContractType.ServiceOnly }
  ];

  startDate: string = '';
  endDate: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    const userSettings = this.authService.getUserSettings();
    const currencyCode = userSettings?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);
    this.distanceSymbol = userSettings?.distanceSymbol ?? 'km';

    this.contractForm = this.fb.group({
      // Step 1 fields
      serviceCenterName: ['', Validators.required],
      financialProviderName: ['', Validators.required],
      contractNumber: ['', Validators.required],
      // Step 2 fields
      terms: [''],
      // Months; only editable once a start date is picked, since it is counted from it
      contractPeriod: [{ value: null, disabled: true }, [Validators.required, Validators.min(this.minContractMonths), Validators.pattern(/^\d+$/)]],
      variableCpk: [''],
      contractMaxKm: [''],
      fixedMonthlyCost: [''],
      contractualKmMonth: ['']
    });

    this.contractPeriodControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.moveEndDateToPeriod());
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onScopesChange(scopes: ScopeSelection[]): void {
    this.selectedAssets = scopes;
  }

  onContractTypeSelect(type: ContractTypeOption): void {
    this.selectedContractType = type;
  }

  onStartDateChange(value: string | undefined): void {
    this.startDate = value ?? '';

    if (!this.startDate) {
      this.contractPeriodControl.disable({ emitEvent: false });
      return;
    }

    this.contractPeriodControl.enable({ emitEvent: false });

    // Keep whichever of the two the user already gave: the period, or else the end date
    if (this.contractMonths !== null) {
      this.moveEndDateToPeriod();
    } else {
      this.updatePeriodFromEndDate();
    }
  }

  onEndDateChange(value: string | undefined): void {
    this.endDate = value ?? '';
    this.updatePeriodFromEndDate();
  }

  get contractTypeLabel(): string {
    return this.transloco.translate(`maintenanceCosting.modals.addContract.${this.selectedContractType?.labelKey ?? 'select.contractType'}`);
  }

  get contractPeriodControl(): AbstractControl {
    return this.contractForm.get('contractPeriod')!;
  }

  get contractMonths(): number | null {
    const value = this.contractPeriodControl.value;
    const months = Number(value);

    return value !== null && value !== '' && Number.isInteger(months) && months >= this.minContractMonths ? months : null;
  }

  get isStep1Valid(): boolean {
    return !!(
      this.selectedAssets.length > 0 &&
      this.trimmed('serviceCenterName') &&
      this.trimmed('financialProviderName') &&
      this.trimmed('contractNumber') &&
      this.selectedContractType
    );
  }

  get canSave(): boolean {
    return !this.isSaving &&
      this.isStep1Valid &&
      this.contractForm.valid &&
      !!this.startDate &&
      !!this.endDate &&
      this.contractMonths !== null &&
      !this.isEndBeforeStart &&
      this.hasValidNumbers;
  }

  get isEndBeforeStart(): boolean {
    const start = parsePickerDate(this.startDate);
    const end = parsePickerDate(this.endDate);

    return !!start && !!end && end <= start;
  }

  private get hasValidNumbers(): boolean {
    return this.numericControls.every(name => !this.trimmed(name) || this.parseAmount(name) !== null);
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.isStep1Valid) {
      this.currentStep = 2;
    }
  }

  previousStep(): void {
    if (this.currentStep === 2) {
      this.currentStep = 1;
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.isSaving = true;

    // The period is not sent: the API works it out from the start and end dates
    const request: AddContractRequest = {
      serviceCenterName: this.trimmed('serviceCenterName'),
      assets: this.selectedAssets.map(asset => ({ assetId: asset.id })),
      financialProviderName: this.trimmed('financialProviderName'),
      contractNumber: this.trimmed('contractNumber'),
      type: this.selectedContractType!.value,
      terms: this.trimmed('terms'),
      startDate: toApiDate(this.startDate),
      endDate: toApiDate(this.endDate),
      variableCpk: this.numberValue('variableCpk'),
      contractMaximumKm: Math.round(this.numberValue('contractMaxKm')),
      fixedMonthlyCost: this.numberValue('fixedMonthlyCost'),
      contractualKmPerMonth: Math.round(this.numberValue('contractualKmMonth'))
    };

    try {
      await this.maintenanceCostingService.AddContractRequest(request, this.cancelSubject);
      this.isSaving = false;
      this.toastr.success(this.transloco.translate('maintenanceCosting.modals.addContract.toasts.added'));
      this.activeModal.close(true);
    } catch (error) {
      this.isSaving = false;
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  private moveEndDateToPeriod(): void {
    const start = parsePickerDate(this.startDate);
    const months = this.contractMonths;

    if (!start || months === null) {
      return;
    }

    // The picker re-emits the new date, which writes the same month count back without re-triggering this
    this.endDate = formatPickerDate(addMonths(start, months));
  }

  private updatePeriodFromEndDate(): void {
    const start = parsePickerDate(this.startDate);
    const end = parsePickerDate(this.endDate);

    if (!start || !end || end <= start) {
      return;
    }

    // Calendar months, the same count as the API's DATEDIFF(MONTH, start, end) that the contracts table shows
    this.contractPeriodControl.setValue(differenceInCalendarMonths(end, start), { emitEvent: false });
  }

  private numberValue(controlName: string): number {
    return this.parseAmount(controlName) ?? 0;
  }

  private parseAmount(controlName: string): number | null {
    const value = parseLocaleNumber(this.contractForm.get(controlName)?.value, this.transloco.getActiveLang());
    return value !== null && value >= 0 ? value : null;
  }

  private trimmed(controlName: string): string {
    return (this.contractForm.get(controlName)?.value ?? '').trim();
  }
}
